import { format, parse } from "date-fns";

import { DATE } from "@utils/constants";

import { OrderFields, DeliveryMethod, InvoiceType, LabelPrint } from "../constants";
import type { TOrderFormData } from "../models/orderForm";
import type { TShipmentRequest } from "../shipmentTypes";

const mapOrderToShipmentRequest = (order: TOrderFormData): TShipmentRequest => {
  const deliveryMethod = order[OrderFields.DELIVERY_METHOD];

  const hasSenderAsContactPerson = order[OrderFields.SENDER_AS_CONTACT_PERSON];

  const senderAddress: TShipmentRequest["senderAddress"] = {
    country: order[OrderFields.STARTING_COUNTRY]!,
    name: order[OrderFields.SENDER_NAME] || null,
    phone: order[OrderFields.SENDER_PHONE_NUMBER]!,
    email: order[OrderFields.SENDER_EMAIL]!,
    isPudo: deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.PudoToDoor,
    ...((deliveryMethod === DeliveryMethod.DoorToDoor || deliveryMethod === DeliveryMethod.DoorToPudo) && {
      street: order[OrderFields.SENDER_STREET],
      streetNo: order[OrderFields.SENDER_HOUSE_NO],
      flatNo: order[OrderFields.SENDER_FLAT_NO],
      city: order[OrderFields.SENDER_CITY],
      postalCode: order[OrderFields.SENDER_ZIP_CODE],
      contactInfo: order[OrderFields.SENDER_COMMENTS],
    }),
    ...((deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.PudoToDoor) && {
      pudoId: order[OrderFields.RETURN_LOCKER],
    }),
    ...(!hasSenderAsContactPerson && {
      contactName: order[OrderFields.CONTACT_PERSON_NAME],
      contactEmail: order[OrderFields.CONTACT_PERSON_EMAIL],
      contactPhone: order[OrderFields.CONTACT_PERSON_PHONE_NUMBER],
    }),
  };
  const receiverAddress: TShipmentRequest["receiverAddress"] = {
    country: order[OrderFields.DESTINATION_COUNTRY]!,
    name: order[OrderFields.RECEIVER_NAME] || null,
    phone: order[OrderFields.RECEIVER_PHONE_NUMBER]!,
    email: order[OrderFields.RECEIVER_EMAIL] || null,
    addressId: order[OrderFields.RECEIVER_ADDRESS_ID] || null,
    isPudo: deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.DoorToPudo,
    ...((deliveryMethod === DeliveryMethod.DoorToDoor || deliveryMethod === DeliveryMethod.PudoToDoor) && {
      street: order[OrderFields.RECEIVER_STREET],
      streetNo: order[OrderFields.RECEIVER_HOUSE_NO],
      flatNo: order[OrderFields.RECEIVER_FLAT_NO],
      city: order[OrderFields.RECEIVER_CITY],
      postalCode: order[OrderFields.RECEIVER_ZIP_CODE],
      contactInfo: order[OrderFields.RECEIVER_COMMENTS],
    }),
    ...((deliveryMethod === DeliveryMethod.PudoToPudo || deliveryMethod === DeliveryMethod.DoorToPudo) && {
      pudoId: order[OrderFields.RECEIVER_LOCKER],
    }),
  };

  const offerGroupId: TShipmentRequest["offerGroupId"] = order[OrderFields.OFFER_GROUP_ID]!;

  const parcelWeight: TShipmentRequest["parcelWeight"] = order[OrderFields.PACKAGE_WEIGHT]!;
  const marketingConsent: TShipmentRequest["marketingConsent"] = order[OrderFields.MARKETING_CONSENT];

  const coupon: TShipmentRequest["coupon"] = order[OrderFields.PROMO_CODE];

  const invoicePrivate =
    order[OrderFields.INVOICE_TYPE] === InvoiceType.PRIVATE
      ? {
          privateName: order[OrderFields.INVOICE_NAME]! || order[OrderFields.SENDER_NAME]!,
          privateEmail: order[OrderFields.INVOICE_EMAIL]! || order[OrderFields.SENDER_EMAIL]!,
        }
      : undefined;

  const invoiceCompany =
    order[OrderFields.INVOICE_TYPE] === InvoiceType.COMPANY
      ? {
          companyName: order[OrderFields.INVOICE_COMPANY_NAME]!,
          emailAddress: order[OrderFields.INVOICE_COMPANY_EMAIL]!,
          registryCode: order[OrderFields.INVOICE_REGISTRY_CODE]!,
          vatNumber: order[OrderFields.INVOICE_VAT_NUMBER],
          companyRegistrationAddress: order[OrderFields.INVOICE_REGISTRATION_ADDRESS]!,
        }
      : undefined;

  const invoice: TShipmentRequest["invoice"] = invoiceCompany || invoicePrivate;

  const formattedPickupDate = order[OrderFields.PICKUP_DATE]
    ? format(parse(order[OrderFields.PICKUP_DATE]!, DATE.DATE_TEMPLATE, new Date()), "yyyy-MM-dd")
    : undefined;

  const pickup: TShipmentRequest["pickup"] =
    (deliveryMethod === DeliveryMethod.DoorToDoor || deliveryMethod === DeliveryMethod.DoorToPudo) &&
    formattedPickupDate
      ? {
          pickupDate: formattedPickupDate,
          pickupTimeFrom: order[OrderFields.PICKUP_TIME_FROM] || undefined,
          pickupTimeTo: order[OrderFields.PICKUP_TIME_TILL] || undefined,
        }
      : undefined;

  const willCustomerPrintLabel: TShipmentRequest["willCustomerPrintLabel"] =
    deliveryMethod === DeliveryMethod.DoorToDoor || deliveryMethod === DeliveryMethod.DoorToPudo
      ? order[OrderFields.LABEL_PRINT] === LabelPrint.PRINT
      : undefined;

  const id: TShipmentRequest["id"] = order[OrderFields.SHIPMENT_ID] || undefined;

  const affiliateGuid: TShipmentRequest["affiliateGuid"] = order[OrderFields.AFFILIATE_ID] || undefined;

  return {
    senderAddress,
    receiverAddress,
    offerGroupId,
    coupon,
    invoice,
    pickup,
    willCustomerPrintLabel,
    id,
    affiliateGuid,
    parcelWeight,
    marketingConsent,
  };
};

export default mapOrderToShipmentRequest;
